import { theme, FONT_RUNE } from "./theme";
import type { Piece, Side } from "../engine";

export interface DrawPieceOpts {
  cell: number;        // cell size in pixels
  cx: number;          // center x
  cy: number;          // center y
  piece: Piece;
  selected?: boolean;
  capturePulse?: number; // 0..1 — flashes red when active
  ghost?: boolean;     // semi-transparent (for animating from)
}

const TWO_PI = Math.PI * 2;

function fillStroke(ctx: CanvasRenderingContext2D, side: Side) {
  ctx.fillStyle = side === "white" ? theme.whiteFill : theme.blackFill;
  ctx.strokeStyle = side === "white" ? theme.whiteEdge : theme.blackEdge;
  ctx.lineWidth = 2;
}

function numberStyle(side: Side) {
  return side === "white" ? theme.whiteNumber : theme.blackNumber;
}

export function drawPiece(ctx: CanvasRenderingContext2D, opts: DrawPieceOpts) {
  const { cell, cx, cy, piece, selected, capturePulse, ghost } = opts;
  ctx.save();
  if (ghost) ctx.globalAlpha = 0.35;

  if (selected) {
    ctx.shadowColor = theme.selected;
    ctx.shadowBlur = cell * 0.25;
  }

  fillStroke(ctx, piece.side);

  const pad = theme.pieceMarginPx * (cell / theme.cellPx);
  const inner = cell - pad * 2;

  switch (piece.shape) {
    case "round": {
      const r = inner / 2;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, TWO_PI);
      ctx.fill();
      ctx.stroke();
      break;
    }
    case "triangle": {
      // Pointing depends on side: white triangles point toward black (toward decreasing rank = up on screen).
      // We'll render with point "up" for white, "down" for black, since on-screen y axis is rank.
      const h = (Math.sqrt(3) / 2) * inner;
      const pointUp = piece.side === "white";
      ctx.beginPath();
      if (pointUp) {
        ctx.moveTo(cx, cy - h * 0.55);
        ctx.lineTo(cx + inner / 2, cy + h * 0.45);
        ctx.lineTo(cx - inner / 2, cy + h * 0.45);
      } else {
        ctx.moveTo(cx, cy + h * 0.55);
        ctx.lineTo(cx + inner / 2, cy - h * 0.45);
        ctx.lineTo(cx - inner / 2, cy - h * 0.45);
      }
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      break;
    }
    case "square": {
      const s = inner;
      ctx.beginPath();
      ctx.rect(cx - s / 2, cy - s / 2, s, s);
      ctx.fill();
      ctx.stroke();
      break;
    }
    case "pyramid": {
      // Stylized: a square base with a triangle peak, plus a small circle at top — evokes the layered piece.
      const s = inner * 0.92;
      const baseY = cy + s * 0.32;
      const topY = cy - s * 0.5;
      // Base
      ctx.beginPath();
      ctx.rect(cx - s / 2, cy - s * 0.18, s, s * 0.5);
      ctx.fill();
      ctx.stroke();
      // Triangle peak
      ctx.beginPath();
      ctx.moveTo(cx, topY);
      ctx.lineTo(cx + s * 0.5, baseY - s * 0.5);
      ctx.lineTo(cx - s * 0.5, baseY - s * 0.5);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      // Crown circle
      ctx.beginPath();
      ctx.arc(cx, topY, s * 0.09, 0, TWO_PI);
      ctx.fill();
      ctx.stroke();
      break;
    }
  }

  ctx.shadowBlur = 0;

  // Capture flash overlay
  if (capturePulse && capturePulse > 0) {
    ctx.globalAlpha = ghost ? 0.35 * capturePulse : capturePulse;
    ctx.fillStyle = theme.captureFlash;
    ctx.beginPath();
    ctx.arc(cx, cy, inner / 1.6, 0, TWO_PI);
    ctx.fill();
    ctx.globalAlpha = ghost ? 0.35 : 1;
  }

  // Number
  ctx.fillStyle = numberStyle(piece.side);
  ctx.font = `600 ${theme.numberSizePx * (cell / theme.cellPx)}px ${FONT_RUNE}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  // Adjust y for shapes with offset visual centers
  let textY = cy;
  if (piece.shape === "pyramid") textY = cy + 0.05 * inner;
  if (piece.shape === "triangle") {
    textY = piece.side === "white" ? cy + 0.08 * inner : cy - 0.08 * inner;
  }
  ctx.fillText(String(piece.value), cx, textY);

  ctx.restore();
}
